/**
 * PlayerContent - Main page-level component
 * Integrates intro, ads, and main content player
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { FocusContext, useFocusable, updateAllLayouts, setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { useLocation, useParams, useNavigate } from 'react-router-dom';

import { PlayerProvider, usePlayer, usePlayerState } from './core/PlayerContext';
import { MnbPlayer } from './ui/MnbPlayer';
import { TopControls, MiddleControls, BottomControls } from './ui/Controls';
import { Settings } from './ui/Settings';
import { EpisodeSwitcher } from './ui/EpisodeSwitcher';
import NextMusicCard from './ui/NextMusicCard';
import ReviewCard from './ui/ReviewCard';
import { getCMSApiRoute, getCMSImageURL } from '../utils/api.utils';
import httpService from '../services/http.service';
import NextPlayGuideHelper from '../utils/nextPlayGuideHelper';
import HotpointHelper from '../utils/hotpointHelper';
import * as ReviewHelper from '../utils/reviewHelper';
import { sendWatchEvent, sendWatchTracker } from '../services/watchTrackerService';
import { refetchContinueWatch } from '../utils/hooks/useHomeShowcaseData';
import {
    createPlaybackSession,
    sendMediaEvent,
    sendSessionEnd,
} from '../services/mediaAnalyticsService';
import { trackGoogleAnalyticsEvent } from '../services/googleAnalytics';
import {
    ADVERTISEMENT_MEDIA_START_TIMEOUT_MS,
    fireTrackingOnce,
    getCreativeTriggerTime,
    getDueCreatives,
    getVastCreatives,
    isSafeHttpUrl,
    openAdvertiserTarget,
    safeActionColor,
    selectMediaFiles,
} from '../services/advertisementService';
import { attachAdMediaSource } from './adMediaPlayback';
import { isHlsSource, orderHlsFirst } from './sourceSelection';

import './styles/player.css';
import './styles/nextContent.css';
import './styles/reviewCard.css';
import './styles/episodeSwitcher.css';

function toFiniteNumber(value) {
    var number = Number(value);
    return isFinite(number) ? number : 0;
}

function isContinueWatchPositionValid(elapsedMinutes, durationSeconds, totalMinutes) {
    var elapsed = toFiniteNumber(elapsedMinutes);
    if (elapsed <= 1) return false;

    var total = toFiniteNumber(totalMinutes);
    if (total > 0) {
        return elapsed < total * 0.95;
    }

    var duration = toFiniteNumber(durationSeconds);
    if (duration > 0) {
        return elapsed * 60 < duration * 0.95;
    }

    return true;
}

function normalizeVastSlot(slot) {
    var slotMap = {
        preRoll: 'pre-roll',
        firstQuartile: 'first-quartile',
        midRoll: 'mid-roll',
        thirdQuartile: 'third-quartile',
    };
    return slotMap[slot] || slot || '';
}

function getAdMediaId(ad, media, mediaIndex) {
    if (!ad) return '';
    if (media && media.id) return media.id;
    if (
        ad.linear &&
        ad.linear.mediaFiles &&
        ad.linear.mediaFiles[mediaIndex] &&
        ad.linear.mediaFiles[mediaIndex].id
    ) {
        return ad.linear.mediaFiles[mediaIndex].id;
    }
    if (ad.mediaFilesData && ad.mediaFilesData[mediaIndex] && ad.mediaFilesData[mediaIndex].id) {
        return ad.mediaFilesData[mediaIndex].id;
    }
    if (ad.mediaIds && ad.mediaIds[mediaIndex]) {
        return ad.mediaIds[mediaIndex];
    }
    var mediaUrl = media && media.url ? media.url : media;
    var fileNameMatch = String(mediaUrl || '').match(/\/([^/?#]+)(?:\/video)?(?:[?#].*)?$/);
    return fileNameMatch ? decodeURIComponent(fileNameMatch[1]) : '';
}

function getAdAnalyticsBaseParams(ad, media, mediaIndex, video, context) {
    var contentData = context?.content || {};
    var adSlot = normalizeVastSlot(ad?.key || ad?.creativeSlot);
    return {
        content_key: contentData.slug || context?.contentKey || '',
        creative_id: ad?.id || ad?.creativeId || '',
        media_id: getAdMediaId(ad, media, mediaIndex),
        campaign_key: ad?.campaignKey || '',
        ad_slot: adSlot,
        spot_position: context?.spotPosition || adSlot,
        ad_position: mediaIndex + 1,
        position_seconds: Math.floor(video?.currentTime || 0),
        content_position_seconds: Math.floor(context?.contentPosition || 0),
        source: context?.source || 'vast_player',
    };
}

function getWatchTotalMinutes(watchTrackData) {
    return toFiniteNumber(
        watchTrackData?.options?.totalTime ||
        watchTrackData?.totalTime ||
        watchTrackData?.options?.duration ||
        watchTrackData?.duration
    );
}

/**
 * Controls Overlay - hides with controls including dpad hint
 * Now includes overlay buttons and next card in the same focus hierarchy
 */
function ControlsOverlay(props) {
    var title = props.title;
    var subtitle = props.subtitle;
    var onSettingsPress = props.onSettingsPress;
    var onCaptionsPress = props.onCaptionsPress;
    var onEpisodesPress = props.onEpisodesPress;
    var showEpisodesButton = props.showEpisodesButton;
    var hideSettings = props.hideSettings;
    var hideCaptions = props.hideCaptions;
    var isLive = props.isLive;
    var children = props.children;

    var controlsVisible = usePlayerState('controlsVisible');

    var { ref, focusKey } = useFocusable({
        focusKey: 'playerControls',
        focusable: false,
        trackChildren: true,
        preferredChildFocusKey: 'middleControls',
    });

    return React.createElement(FocusContext.Provider, { value: focusKey },
        React.createElement('div', {
            className: 'mnb-controls-overlay ' + (controlsVisible ? 'visible' : 'hidden'),
            ref: ref,
        },
            React.createElement(TopControls, {
                title: title,
                subtitle: subtitle,
                isLive: isLive,
            }),
            React.createElement(MiddleControls, { isLive: isLive }),
            React.createElement(BottomControls, {
                onSettingsPress: onSettingsPress,
                onCaptionsPress: onCaptionsPress,
                onEpisodesPress: onEpisodesPress,
                showEpisodesButton: showEpisodesButton,
                hideSettings: hideSettings,
                hideCaptions: hideCaptions,
                isLive: isLive,
            }),
            children
        )
    );
}

/**
 * Skip Intro Button (Titrage Handler)
 */
function SkipIntroButton(props) {
    var hotPoints = props.hotPoints;
    var onSkip = props.onSkip;
    var player = usePlayer();
    var currentTime = usePlayerState('currentTime');
    var [titrage, setTitrage] = useState(null);
    var [showButton, setShowButton] = useState(false);

    var leaveSkipButton = function () {
        setTimeout(function () {
            setFocus('playButton');
        }, 0);
    };

    var handleSkip = function () {
        if (!titrage) return;
        player.seek(titrage.endTime);
        if (onSkip) onSkip();
        leaveSkipButton();
    };

    var { focused, ref, focusSelf, focusKey } = useFocusable({
        focusKey: 'skipIntroBtn',
        focusable: showButton,
        onEnterPress: handleSkip,
        onArrowPress: function (direction) {
            if (direction === 'up') {
                setFocus('seekBackward');
                return false;
            }
            if (direction === 'down') {
                setFocus('progressBar');
                return false;
            }
            return true;
        },
    });

    useEffect(function () {
        var titragePoint = hotPoints && hotPoints.find(function (x) {
            return x.hotpointType === 'intro';
        });
        setTitrage(titragePoint || null);
    }, [hotPoints]);

    useEffect(function () {
        if (titrage && currentTime > titrage.startTime && currentTime < titrage.endTime) {
            if (!showButton) {
                setShowButton(true);
            }
        } else {
            if (showButton) {
                setShowButton(false);
            }
        }
    }, [titrage, currentTime, showButton]);

    // Auto-focus skip intro button when it becomes visible (Flutter TV standard)
    useEffect(function () {
        if (showButton && focusSelf) {
            // Delay to ensure DOM is ready and focusable tree is updated
            var timer = setTimeout(function () {
                focusSelf();
                console.log('[SkipIntro] Auto-focused skip intro button');
            }, 100);
            return function () { clearTimeout(timer); };
        }
    }, [showButton, focusSelf]);

    // Update spatial navigation tree when button visibility changes
    useEffect(function () {
        updateAllLayouts();
        if (!showButton && focused) {
            leaveSkipButton();
        }
    }, [showButton, focused]);

    // Always render FocusContext.Provider, only hide content conditionally
    return React.createElement(FocusContext.Provider, { value: focusKey },
        showButton ? React.createElement('button', {
            ref: ref,
            className: 'mnb-overlay-btn ' + (focused ? 'focused' : ''),
            onClick: handleSkip,
            type: 'button',
        }, 'رد کردن تیتراژ') : null
    );
}

/**
 * Continue Watch Button
 */
function ContinueWatchButton(props) {
    var elapsedTime = props.elapsedTime;
    var totalTime = props.totalTime;
    var player = usePlayer();
    var currentTime = usePlayerState('currentTime');
    var duration = usePlayerState('duration');
    var [showButton, setShowButton] = useState(false);
    var [hasUsed, setHasUsed] = useState(false);
    var isValidContinueWatch = isContinueWatchPositionValid(elapsedTime, duration, totalTime);

    var seekToContinueWatch = function () {
        player.seek(Math.max(0, elapsedTime * 60 - 5));
        setHasUsed(true);
    };

    var { focused, ref, focusKey } = useFocusable({
        focusKey: 'continueWatchBtn',
        focusable: showButton && !hasUsed,
        onEnterPress: seekToContinueWatch,
    });

    useEffect(function () {
        if (isValidContinueWatch && currentTime < 60 && !hasUsed) {
            if (!showButton) {
                setShowButton(true);
            }
        } else {
            if (showButton) {
                setShowButton(false);
            }
        }
    }, [isValidContinueWatch, currentTime, showButton, hasUsed]);

    // Update spatial navigation tree when button visibility changes
    useEffect(function () {
        updateAllLayouts();
    }, [showButton]);

    var isVisible = showButton && !hasUsed;

    // Always render FocusContext.Provider, only hide content conditionally
    return React.createElement(FocusContext.Provider, { value: focusKey },
        isVisible ? React.createElement('button', {
            ref: ref,
            className: 'mnb-overlay-btn ' + (focused ? 'focused' : ''),
            onClick: seekToContinueWatch,
            type: 'button',
        }, 'ادامه تماشا') : null
    );
}

/**
 * AutoResumeHandler - Automatically seeks to saved position when player is ready
 * Used when autoResume is enabled to provide seamless continue-watch experience
 * Skips showing the manual ContinueWatchButton - seeks automatically
 */
function AutoResumeHandler(props) {
    var elapsedTime = props.elapsedTime;
    var totalTime = props.totalTime;
    var onAutoResume = props.onAutoResume;
    var player = usePlayer();
    var isReady = usePlayerState('isReady');
    var duration = usePlayerState('duration');
    var hasResumedRef = useRef(false);
    var isValidContinueWatch = isContinueWatchPositionValid(elapsedTime, duration, totalTime);

    useEffect(function () {
        if (!elapsedTime || elapsedTime <= 0 || hasResumedRef.current) return;
        if (!isValidContinueWatch) {
            console.log('[AutoResume] Skipping saved position near end:', elapsedTime, 'minutes');
            return;
        }

        // Wait for player to be ready
        if (isReady) {
            hasResumedRef.current = true;
            // Seek to 5 seconds before the saved position for context
            var seekPosition = Math.max(0, (elapsedTime * 60) - 5);
            console.log('[AutoResume] Auto-seeking to position:', seekPosition, 'seconds');
            player.seek(seekPosition);
            if (onAutoResume) onAutoResume();
        }
    }, [elapsedTime, isReady, isValidContinueWatch, player, onAutoResume]);

    return null; // This is a logic-only component
}

/**
 * Next Episode Card
 */
function NextEpisodeCard(props) {
    var nextEpisode = props.nextEpisode;
    var slug = props.slug;
    var visible = props.visible;
    var onDismiss = props.onDismiss;
    var navigate = useNavigate();
    var autoPlaySeconds = 10;
    var [remainingSeconds, setRemainingSeconds] = useState(autoPlaySeconds);

    var isVisible = visible && nextEpisode;

    var playNext = useCallback(function () {
        if (nextEpisode) {
            navigate('/watch/' + slug + '/' + nextEpisode.seasonNumber + '/' + nextEpisode.episodeNumber, { replace: true });
        }
    }, [navigate, nextEpisode, slug]);

    var { focused, ref, focusKey, focusSelf } = useFocusable({
        focusKey: 'nextEpisodeCard',
        focusable: isVisible,
        onEnterPress: playNext,
    });

    var { ref: dismissRef, focused: dismissFocused } = useFocusable({
        focusKey: 'nextEpisodeDismissBtn',
        focusable: isVisible,
        onEnterPress: function () {
            if (onDismiss) onDismiss();
        },
    });
    // Update spatial navigation tree when visibility changes
    useEffect(function () {
        updateAllLayouts();
    }, [isVisible]);

    useEffect(function () {
        if (!isVisible) {
            setRemainingSeconds(autoPlaySeconds);
            return;
        }

        setRemainingSeconds(autoPlaySeconds);

        var timeout = setTimeout(function () {
            playNext();
        }, autoPlaySeconds * 1000);

        var interval = setInterval(function () {
            setRemainingSeconds(function (seconds) {
                return Math.max(0, seconds - 1);
            });
        }, 1000);

        return function () {
            clearTimeout(timeout);
            clearInterval(interval);
        };
    }, [autoPlaySeconds, isVisible, playNext]);

    // Auto-focus next episode card when it becomes visible
    useEffect(function () {
        if (isVisible && focusSelf) {
            var timer = setTimeout(function () {
                focusSelf();
                console.log('[NextEpisode] Auto-focused next episode card');
            }, 100);
            return function () { clearTimeout(timer); };
        }
    }, [isVisible, focusSelf]);

    // Image Logic: Memoize to prevent recomputation on every render
    var imageUrl = useMemo(function () {
        if (!isVisible || !nextEpisode) return null;

        // Check nested path: data.meta.contentPhotos (API structure from user)
        var photos = nextEpisode.data?.meta?.contentPhotos ||
            nextEpisode.meta?.contentPhotos ||
            nextEpisode.contentPhotos ||
            [];

        if (photos && photos.length > 0) {
            // Try episodeCover with TV device preference for best quality
            var cover = photos.find(function (p) {
                return p.type === 'episodeCover' && p.device === 'TV';
            });

            // Fallback to any episodeCover
            if (!cover) {
                cover = photos.find(function (p) { return p.type === 'episodeCover'; });
            }

            // Fallback to horizontalCover with TV preference
            if (!cover) {
                cover = photos.find(function (p) {
                    return p.type === 'horizontalCover' && p.device === 'TV';
                });
            }

            // Fallback to any horizontalCover
            if (!cover) {
                cover = photos.find(function (p) { return p.type === 'horizontalCover'; });
            }

            // Final fallback to first photo
            if (!cover) {
                cover = photos[0];
            }

            if (cover) {
                var imagePath = cover.webp || cover.src || cover.path;
                if (imagePath) {
                    return getCMSImageURL(imagePath);
                }
            }
        }

        // Direct poster fallback (some API responses have this)
        if (nextEpisode.poster) {
            return getCMSImageURL(nextEpisode.poster);
        }

        console.warn('[NextEpisode] No cover found for next episode');
        return null;
    }, [isVisible, nextEpisode]);

    // Always render FocusContext.Provider, only hide content conditionally
    return React.createElement(FocusContext.Provider, { value: focusKey },
        isVisible ? React.createElement('div', {
            ref: ref,
            className: 'mnb-next-content-card ' + (focused ? 'focused' : ''),
            onClick: playNext,
            style: imageUrl ? {
                backgroundImage: 'url(' + imageUrl + ')',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                aspectRatio: '16/9'
            } : { aspectRatio: '16/9' },
        },
            React.createElement('button', {
                ref: dismissRef,
                type: 'button',
                'aria-label': 'بستن',
                onClick: function (event) {
                    event.stopPropagation();
                    if (onDismiss) onDismiss();
                },
                style: {
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    zIndex: 2,
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'rgba(0, 0, 0, 0.65)',
                    color: '#fff',
                    border: dismissFocused ? '3px solid #fff' : '1px solid rgba(255,255,255,0.35)',
                    fontSize: '24px',
                    lineHeight: '30px',
                },
            }, '×'),
            React.createElement('span', {
                className: 'mnb-next-content-timer',
                style: {
                    position: 'absolute',
                    left: '12px',
                    bottom: '12px',
                    zIndex: 2,
                    background: 'rgba(0, 0, 0, 0.82)',
                    color: '#ffffff',
                    border: '1px solid rgba(255, 255, 255, 0.35)',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.55)',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.9)',
                    fontWeight: 700,
                },
            }, remainingSeconds + ' ثانیه'),
            React.createElement('div', { className: 'mnb-next-card-content' },
                React.createElement('h5', { className: 'mnb-next-title' }, 'قسمت بعدی'),
                React.createElement('p', { className: 'mnb-next-info' },
                    'فصل ' + nextEpisode.seasonNumber + ' قسمت ' + nextEpisode.episodeNumber
                )
            )
        ) : null
    );
}

/**
 * Loading Indicator - Red top bar beneath title
 */
function LoadingIndicator() {
    var loading = usePlayerState('loading');
    var waiting = usePlayerState('waiting');

    if (!loading && !waiting) return null;

    return React.createElement('div', { className: 'mnb-top-loading' },
        React.createElement('div', { className: 'mnb-top-loading-bar' })
    );
}

/**
 * Check if running on TV platform
 */
function isTV() {
    if (typeof window === 'undefined') return false;
    var ua = navigator.userAgent.toLowerCase();
    return ua.includes('webos') || ua.includes('tizen') || ua.includes('smarttv');
}

/**
 * Simulate keypress
 */
function simulateKeypress(keyCode) {
    var event = new KeyboardEvent('keydown', { keyCode: keyCode });
    window.dispatchEvent(event);
}

/**
 * Player DPad - only visible when controls are visible
 */
function PlayerDPad() {
    var controlsVisible = usePlayerState('controlsVisible');

    // Don't show on actual TV platforms (they have physical remotes)
    if (isTV()) return null;

    return React.createElement('div', {
        className: 'mnb-player-dpad ' + (controlsVisible ? '' : 'hidden'),
    },
        React.createElement('div', { className: 'mnb-dpad-container' },
            React.createElement('button', {
                className: 'mnb-dpad-btn mnb-dpad-up',
                onClick: function () { simulateKeypress(38); },
                type: 'button',
            }, React.createElement('span', { className: 'mnb-dpad-icon' }, '▲')),

            React.createElement('button', {
                className: 'mnb-dpad-btn mnb-dpad-right',
                onClick: function () { simulateKeypress(39); },
                type: 'button',
            }, React.createElement('span', { className: 'mnb-dpad-icon' }, '▶')),

            React.createElement('button', {
                className: 'mnb-dpad-center',
                onClick: function () { simulateKeypress(13); },
                type: 'button',
            }, React.createElement('span', { className: 'mnb-dpad-center-text' }, 'OK')),

            React.createElement('button', {
                className: 'mnb-dpad-btn mnb-dpad-left',
                onClick: function () { simulateKeypress(37); },
                type: 'button',
            }, React.createElement('span', { className: 'mnb-dpad-icon' }, '◀')),

            React.createElement('button', {
                className: 'mnb-dpad-btn mnb-dpad-down',
                onClick: function () { simulateKeypress(40); },
                type: 'button',
            }, React.createElement('span', { className: 'mnb-dpad-icon' }, '▼'))
        )
    );
}

/**
 * Intro Player - plays intro video before main content
 * No skip button - controlled by behavior.playIntro
 */
function IntroPlayer(props) {
    var onPlayEnd = props.onPlayEnd;
    var poster = props.poster;

    var videoRef = useRef(null);
    var onPlayEndRef = useRef(onPlayEnd);
    var hasEndedRef = useRef(false);
    var [isPlaying, setIsPlaying] = useState(false);
    var [controlsVisible, setControlsVisible] = useState(true);
    var hideTimeoutRef = useRef(null);

    // Keep callback ref updated
    useEffect(function () {
        onPlayEndRef.current = onPlayEnd;
    }, [onPlayEnd]);

    var { ref, focusSelf } = useFocusable({
        focusKey: 'introPlayer',
    });

    useEffect(function () {
        focusSelf();
    }, [focusSelf]);

    // Auto-hide controls
    var showControls = useCallback(function () {
        setControlsVisible(true);
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
        }
        hideTimeoutRef.current = setTimeout(function () {
            if (isPlaying) {
                setControlsVisible(false);
            }
        }, 5000);
    }, [isPlaying]);

    // Initialize video - no dependencies on callbacks to prevent re-init
    useEffect(function () {
        var video = videoRef.current;
        if (!video) return;

        console.log('[IntroPlayer] Initializing...');

        // Set source
        video.src = '/intro.mp4';
        video.load();

        // Event handlers
        var handleCanPlay = function () {
            console.log('[IntroPlayer] Can play, starting...');
            video.play().catch(function (err) {
                console.warn('[IntroPlayer] Autoplay failed:', err);
                // Try muted
                video.muted = true;
                video.play().catch(function () {
                    console.error('[IntroPlayer] Muted autoplay also failed, skipping intro');
                    if (!hasEndedRef.current && onPlayEndRef.current) {
                        hasEndedRef.current = true;
                        onPlayEndRef.current();
                    }
                });
            });
        };

        var handlePlay = function () {
            setIsPlaying(true);
        };

        var handlePause = function () {
            setIsPlaying(false);
            setControlsVisible(true);
        };

        var handleEnded = function () {
            console.log('[IntroPlayer] Ended event fired');
            if (!hasEndedRef.current && onPlayEndRef.current) {
                hasEndedRef.current = true;
                onPlayEndRef.current();
            }
        };

        // Fallback for older Tizen/WebOS where 'ended' event doesn't fire reliably
        var handleTimeUpdate = function () {
            if (hasEndedRef.current) return;
            var duration = video.duration;
            var currentTime = video.currentTime;
            // Trigger when within 0.5 second of end
            if (duration && currentTime && duration > 0 && (duration - currentTime) < 0.5) {
                console.log('[IntroPlayer] TimeUpdate fallback - near end, triggering callback');
                if (!hasEndedRef.current && onPlayEndRef.current) {
                    hasEndedRef.current = true;
                    onPlayEndRef.current();
                }
            }
        };

        var handleError = function (e) {
            console.error('[IntroPlayer] Error:', e);
            // Skip to main content on error
            if (!hasEndedRef.current && onPlayEndRef.current) {
                hasEndedRef.current = true;
                onPlayEndRef.current();
            }
        };

        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('error', handleError);

        return function () {
            console.log('[IntroPlayer] Cleanup - forcing video stop for legacy Chromium');
            video.removeEventListener('canplay', handleCanPlay);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('ended', handleEnded);
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('error', handleError);

            // Critical for older Chromium (Tizen/WebOS): force stop and release video
            try {
                video.pause();
                video.currentTime = 0;
                video.src = '';
                video.removeAttribute('src');
                video.load(); // This releases the video resource on legacy browsers
            } catch (e) {
                console.warn('[IntroPlayer] Cleanup error:', e);
            }

            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
        };
    }, []); // Empty deps - only run once

    // Handle key presses
    useEffect(function () {
        var handleKeyDown = function (e) {
            showControls();

            if (e.key === 'Enter' || e.keyCode === 13) {
                var video = videoRef.current;
                if (video) {
                    if (video.paused) {
                        video.play();
                    } else {
                        video.pause();
                    }
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return function () {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [showControls]);

    return React.createElement('div', {
        ref: ref,
        className: 'mnb-player-container',
        onClick: showControls,
    },
        React.createElement('video', {
            ref: videoRef,
            className: 'mnb-player-video',
            poster: poster,
            playsInline: true,
        }),
        React.createElement('div', {
            className: 'mnb-controls-overlay ' + (controlsVisible ? 'visible' : 'hidden'),
        },
            React.createElement('div', { className: 'mnb-controls-top' }),
            React.createElement('div', { className: 'mnb-controls-middle' },
                React.createElement('button', {
                    className: 'mnb-btn mnb-play-btn',
                    onClick: function () {
                        var video = videoRef.current;
                        if (video) {
                            if (video.paused) video.play();
                            else video.pause();
                        }
                    },
                },
                    React.createElement('svg', { width: 40, height: 40, viewBox: '0 0 24 24', fill: 'currentColor' },
                        isPlaying
                            ? React.createElement('path', { d: 'M6 4h4v16H6V4zm8 0h4v16h-4V4z' })
                            : React.createElement('path', { d: 'M8 5v14l11-7z' })
                    )
                )
            ),
            React.createElement('div', { className: 'mnb-controls-bottom' })
        )
    );
}

/**
 * VAST Ad Player - plays advertisement videos
 * Supports sequential playback of multiple ads and skip button
 */
function VastPlayer(props) {
    var vast = props.vast;
    var creative = props.creative;
    var analyticsContext = props.analyticsContext;
    var sessionFired = props.sessionFired;
    var onPlayEnd = props.onPlayEnd;
    var allowSkip = props.allowSkip;
    var skipValue = props.skipValue || 5;

    var playSources = useMemo(function () {
        return selectMediaFiles(creative);
    }, [creative]);

    var videoRef = useRef(null);
    var [currentSourceIndex, setCurrentSourceIndex] = useState(0);
    var [showSkip, setShowSkip] = useState(false);
    var [modalTarget, setModalTarget] = useState(null);
    var hasEndedRef = useRef(false);
    var showSkipRef = useRef(false);
    var analyticsContextRef = useRef(analyticsContext || {});

    var { ref, focusKey, focusSelf } = useFocusable({
        focusKey: 'vastPlayer',
    });

    useEffect(function () {
        focusSelf();
    }, [focusSelf]);

    useEffect(function () {
        showSkipRef.current = showSkip;
    }, [showSkip]);

    useEffect(function () {
        analyticsContextRef.current = analyticsContext || {};
    }, [analyticsContext]);

    var trackVastEvent = useCallback(function (vastEvent, video, media, mediaIndex, explicitUrl) {
        var didFire = fireTrackingOnce(
            sessionFired,
            vast,
            creative,
            vastEvent,
            explicitUrl
        );
        if (!didFire) return false;
        var params = Object.assign(
            getAdAnalyticsBaseParams(creative, media, mediaIndex, video, analyticsContextRef.current),
            { vast_event: vastEvent }
        );

        trackGoogleAnalyticsEvent('ad_event', params);
        if (vastEvent === 'impression') {
            trackGoogleAnalyticsEvent('ad_impression', params);
        }
        return true;
    }, [creative, sessionFired, vast]);

    useEffect(function () {
        if (!vast || !creative || playSources.length === 0) return;
        trackVastEvent(
            'impression',
            videoRef.current,
            playSources[0],
            0,
            vast?.ad?.inline?.impression
        );
        trackVastEvent('creative_view', videoRef.current, playSources[0], 0);
    }, [creative, playSources, trackVastEvent, vast]);

    // Handle video end - move to next ad or finish
    var handleVideoEnd = useCallback(function () {
        if (hasEndedRef.current) return;

        if (currentSourceIndex < playSources.length - 1) {
            setCurrentSourceIndex(currentSourceIndex + 1);
            showSkipRef.current = false;
            setShowSkip(false);
        } else {
            hasEndedRef.current = true;
            if (onPlayEnd) onPlayEnd();
        }
    }, [currentSourceIndex, playSources.length, onPlayEnd]);

    // Handle skip button
    var handleSkip = useCallback(function () {
        var video = videoRef.current;
        var currentMedia = playSources[currentSourceIndex];
        trackVastEvent('skip', video, currentMedia, currentSourceIndex);
        hasEndedRef.current = true;
        if (onPlayEnd) onPlayEnd();
    }, [currentSourceIndex, onPlayEnd, playSources, trackVastEvent]);

    // Skip button focusable
    var { focused: skipFocused, ref: skipRef, focusSelf: focusSkip } = useFocusable({
        focusKey: 'vastSkipBtn',
        focusable: showSkip,
        onEnterPress: handleSkip,
    });

    // Auto-focus skip button when it appears
    useEffect(function () {
        if (showSkip && focusSkip) {
            var timer = setTimeout(function () {
                focusSkip();
            }, 100);
            return function () { clearTimeout(timer); };
        }
    }, [showSkip, focusSkip]);

    // Initialize video
    useEffect(function () {
        var video = videoRef.current;
        if (!video || playSources.length === 0) return;

        hasEndedRef.current = false;
        showSkipRef.current = false;
        setShowSkip(false);
        var currentMedia = playSources[currentSourceIndex];
        var currentAdUrl = currentMedia.url;
        var hasPlayedCurrentMedia = false;
        var lastPlaybackTime = 0;
        var mediaStartTimer = setTimeout(function () {
            console.warn('[VastPlayer] Ad media start timed out, failing open');
            handleVideoEnd();
        }, ADVERTISEMENT_MEDIA_START_TIMEOUT_MS);
        console.log('[VastPlayer] Loading ad:', currentSourceIndex + 1, 'of', playSources.length, currentAdUrl);

        var handleCanPlay = function () {
            console.log('[VastPlayer] Ad can play, starting...');
            video.play().catch(function (err) {
                console.warn('[VastPlayer] Autoplay failed:', err);
                video.muted = true;
                video.play().catch(function () {
                    console.error('[VastPlayer] Muted autoplay failed, skipping ad');
                    handleVideoEnd();
                });
            });
        };

        var handlePlay = function () {
            clearTimeout(mediaStartTimer);
            var didStart = trackVastEvent(
                'start',
                video,
                currentMedia,
                currentSourceIndex
            );
            if (!didStart && hasPlayedCurrentMedia) {
                trackVastEvent(
                    'resume',
                    video,
                    currentMedia,
                    currentSourceIndex
                );
            }
            hasPlayedCurrentMedia = true;
        };

        var handlePause = function () {
            if (hasPlayedCurrentMedia && !video.ended) {
                trackVastEvent(
                    'pause',
                    video,
                    currentMedia,
                    currentSourceIndex
                );
            }
        };

        var handleVolumeChange = function () {
            trackVastEvent(
                video.muted ? 'mute' : 'unmute',
                video,
                currentMedia,
                currentSourceIndex
            );
        };

        var handleSeeking = function () {
            if (video.currentTime < lastPlaybackTime) {
                trackVastEvent(
                    'rewind',
                    video,
                    currentMedia,
                    currentSourceIndex
                );
            }
        };

        var handleTimeUpdate = function () {
            var duration = video.duration || 0;
            if (duration > 0) {
                var progress = video.currentTime / duration;
                if (progress >= 0.25) {
                    trackVastEvent('first_quartile', video, currentMedia, currentSourceIndex);
                }
                if (progress >= 0.5) {
                    trackVastEvent('midpoint', video, currentMedia, currentSourceIndex);
                }
                if (progress >= 0.75) {
                    trackVastEvent('third_quartile', video, currentMedia, currentSourceIndex);
                }
            }
            lastPlaybackTime = video.currentTime;
            if (allowSkip && video.currentTime >= skipValue && !showSkipRef.current) {
                showSkipRef.current = true;
                setShowSkip(true);
            }
        };

        var handleEnded = function () {
            console.log('[VastPlayer] Ad ended');
            if (currentSourceIndex === playSources.length - 1) {
                trackVastEvent('complete', video, currentMedia, currentSourceIndex);
            }
            handleVideoEnd();
        };

        var handleVideoClick = function () {
            var clicks = creative?.linear?.videoClicks || {};
            if (!isSafeHttpUrl(clicks.clickThrough)) return;
            trackVastEvent(
                'video_click',
                video,
                currentMedia,
                currentSourceIndex,
                clicks.clickTracking
            );
            openAdvertiserTarget(clicks.clickThrough);
        };

        var handleError = function (e) {
            clearTimeout(mediaStartTimer);
            console.error('[VastPlayer] Error:', e);
            handleVideoEnd();
        };

        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('volumechange', handleVolumeChange);
        video.addEventListener('seeking', handleSeeking);
        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('ended', handleEnded);
        var clicks = creative?.linear?.videoClicks || {};
        var hasClickThrough = isSafeHttpUrl(clicks.clickThrough);
        if (hasClickThrough) {
            video.addEventListener('click', handleVideoClick);
        }
        video.addEventListener('error', handleError);
        var detachMediaSource = attachAdMediaSource(
            video,
            currentMedia,
            handleError
        );

        return function () {
            clearTimeout(mediaStartTimer);
            detachMediaSource();
            video.removeEventListener('canplay', handleCanPlay);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('volumechange', handleVolumeChange);
            video.removeEventListener('seeking', handleSeeking);
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('ended', handleEnded);
            if (hasClickThrough) {
                video.removeEventListener('click', handleVideoClick);
            }
            video.removeEventListener('error', handleError);
        };
    }, [playSources, currentSourceIndex, allowSkip, skipValue, handleVideoEnd, trackVastEvent, creative]);

    useEffect(function () {
        var wasExpanded = false;
        var handleFullscreenChange = function () {
            var isExpanded = Boolean(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            );
            if (isExpanded && !wasExpanded) {
                trackVastEvent(
                    'expand',
                    videoRef.current,
                    playSources[currentSourceIndex],
                    currentSourceIndex
                );
            } else if (!isExpanded && wasExpanded) {
                trackVastEvent(
                    'collapse',
                    videoRef.current,
                    playSources[currentSourceIndex],
                    currentSourceIndex
                );
            }
            wasExpanded = isExpanded;
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);
        return function () {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, [currentSourceIndex, playSources, trackVastEvent]);

    var actionLink = isSafeHttpUrl(creative?.extActionLink)
        ? creative.extActionLink
        : null;
    var modalBannerUrl = isSafeHttpUrl(creative?.extModalBannerUrl)
        ? creative.extModalBannerUrl
        : null;
    var actionLabel = actionLink
        ? (creative?.extActionLabel
            ? String(creative.extActionLabel)
            : 'مشاهده')
        : null;
    var actionColor = safeActionColor(creative?.extActionButtonColor);

    var handleAction = useCallback(function () {
        if (!actionLink) return;
        var currentMedia = playSources[currentSourceIndex];
        var clicks = creative?.linear?.videoClicks || {};
        trackVastEvent(
            'video_click',
            videoRef.current,
            currentMedia,
            currentSourceIndex,
            clicks.clickTracking
        );
        if (modalBannerUrl) {
            setModalTarget(actionLink);
        } else {
            openAdvertiserTarget(actionLink);
        }
    }, [actionLink, creative, currentSourceIndex, modalBannerUrl, playSources, trackVastEvent]);

    var { focused: ctaFocused, ref: ctaRef } = useFocusable({
        focusKey: 'vastCtaBtn',
        focusable: Boolean(actionLabel),
        onEnterPress: handleAction,
    });

    var handleModalOpen = useCallback(function () {
        if (modalTarget) {
            openAdvertiserTarget(modalTarget);
        }
        setModalTarget(null);
    }, [modalTarget]);

    var { focused: modalOpenFocused, ref: modalOpenRef } = useFocusable({
        focusKey: 'vastModalOpenBtn',
        focusable: Boolean(modalTarget),
        onEnterPress: handleModalOpen,
    });
    var { focused: modalCloseFocused, ref: modalCloseRef } = useFocusable({
        focusKey: 'vastModalCloseBtn',
        focusable: Boolean(modalTarget),
        onEnterPress: function () { setModalTarget(null); },
    });

    useEffect(function () {
        if (modalTarget) {
            setFocus('vastModalOpenBtn');
        }
    }, [modalTarget]);

    // Update spatial navigation when skip becomes available
    useEffect(function () {
        updateAllLayouts();
    }, [showSkip, actionLabel, modalTarget]);

    // If no ads, skip immediately
    useEffect(function () {
        if (playSources.length === 0 && onPlayEnd) {
            onPlayEnd();
        }
    }, [playSources.length, onPlayEnd]);

    if (playSources.length === 0) {
        return null;
    }

    return React.createElement(FocusContext.Provider, { value: focusKey },
        React.createElement('div', {
            ref: ref,
            className: 'mnb-vast-container',
        },
            React.createElement('video', {
                ref: videoRef,
                className: 'mnb-player-video',
                'aria-label': 'تبلیغات',
                playsInline: true,
                preload: 'auto',
                crossOrigin: 'anonymous',
            }),
            // Ad info overlay
            React.createElement('div', { className: 'mnb-vast-info' },
                React.createElement('span', { className: 'mnb-vast-label' }, 'تبلیغات'),
                playSources.length > 1 && React.createElement('span', { className: 'mnb-vast-counter' },
                    (currentSourceIndex + 1) + ' از ' + playSources.length
                )
            ),
            // Skip button
            showSkip && React.createElement('button', {
                ref: skipRef,
                className: 'mnb-vast-skip-btn ' + (skipFocused ? 'focused' : ''),
                onClick: handleSkip,
                type: 'button',
            },
                React.createElement('span', null, 'رد کردن تبلیغ'),
                React.createElement('svg', { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'currentColor' },
                    React.createElement('path', { d: 'M6 18l8.5-6L6 6v12zm2-8.14L11.03 12 8 14.14V9.86zM16 6h2v12h-2V6z' })
                )
            ),
            actionLabel && React.createElement('button', {
                ref: ctaRef,
                className: 'mnb-vast-cta-btn ' + (ctaFocused ? 'focused' : ''),
                onClick: handleAction,
                type: 'button',
                style: actionColor ? { backgroundColor: actionColor } : undefined,
            }, actionLabel),
            modalTarget && modalBannerUrl && React.createElement('div', {
                className: 'mnb-vast-modal',
                role: 'dialog',
                'aria-modal': 'true',
            },
                React.createElement('div', { className: 'mnb-vast-modal-content' },
                    React.createElement('img', {
                        className: 'mnb-vast-modal-banner',
                        src: modalBannerUrl,
                        alt: actionLabel || 'تبلیغات',
                    }),
                    React.createElement('div', { className: 'mnb-vast-modal-actions' },
                        React.createElement('button', {
                            ref: modalOpenRef,
                            className: 'mnb-vast-modal-btn ' + (modalOpenFocused ? 'focused' : ''),
                            onClick: handleModalOpen,
                            type: 'button',
                        }, actionLabel || 'مشاهده'),
                        React.createElement('button', {
                            ref: modalCloseRef,
                            className: 'mnb-vast-modal-btn ' + (modalCloseFocused ? 'focused' : ''),
                            onClick: function () { setModalTarget(null); },
                            type: 'button',
                        }, 'بستن')
                    )
                )
            )
        )
    );
}

/**
 * Ad Handler - monitors playback time and triggers ads at quartile points
 */
function AdHandler(props) {
    var currentTime = props.currentTime;
    var duration = props.duration;
    var creatives = props.creatives;
    var scheduledCreativeIds = props.scheduledCreativeIds;
    var adActive = props.adActive;
    var onTriggerAds = props.onTriggerAds;

    useEffect(function () {
        if (!creatives || adActive) return;
        var dueCreatives = getDueCreatives(
            creatives,
            currentTime,
            duration,
            scheduledCreativeIds
        );
        if (dueCreatives.length > 0) {
            console.log('[AdHandler] Triggering', dueCreatives.length, 'creative(s) at', currentTime);
            onTriggerAds(dueCreatives, currentTime);
        }
    }, [
        adActive,
        creatives,
        currentTime,
        duration,
        onTriggerAds,
        scheduledCreativeIds,
    ]);

    return null;
}

/**
 * Main Video Player with all features
 */
function VideoPlayerWrapper(props) {
    var source = props.source;
    var subtitle = props.subtitle;
    var hotPoints = props.hotPoints;
    var rawData = props.rawData;
    var poster = props.poster;
    var watchTrackData = props.watchTrackData;
    var onEnded = props.onEnded;
    var onError = props.onError;
    var autoResume = props.autoResume;
    var isTrailer = props.isTrailer || rawData?.isTrailer;
    var onPlayingChange = props.onPlayingChange;
    var isLive = props.isLive === true || props.isLive === 1 || rawData?.isLive === 1 || rawData?.isLive === true;
    // Ad-related props
    var adCreatives = props.adCreatives;
    var scheduledCreativeIds = props.scheduledCreativeIds;
    var adActive = props.adActive;
    var onTriggerAds = props.onTriggerAds;
    var resumeTime = props.resumeTime;

    var [settingsOpen, setSettingsOpen] = useState(false);
    var [captionsOpen, setCaptionsOpen] = useState(false);
    var [episodesOpen, setEpisodesOpen] = useState(false);
    var params = useParams();
    var navigate = useNavigate();
    var location = useLocation();
    var entryPointGuide = useMemo(function () {
        return NextPlayGuideHelper.getGuideFromNavigationState(location.state);
    }, [location.state]);
    var player = usePlayer();
    var paused = usePlayerState('paused');
    var playing = usePlayerState('playing');
    var waiting = usePlayerState('waiting');
    var seeking = usePlayerState('seeking');
    var ended = usePlayerState('ended');
    var duration = usePlayerState('duration');
    var currentTime = usePlayerState('currentTime');
    var currentQuality = usePlayerState('currentQuality');
    var error = usePlayerState('error');
    var isReady = usePlayerState('isReady');
    var textTracks = usePlayerState('textTracks');
    var audioTracks = usePlayerState('audioTracks');
    var defaultSubAppliedRef = useRef(false);
    var defaultAudioAppliedRef = useRef(false);
    var playbackSessionIdRef = useRef(createPlaybackSession());
    var sentManifestRef = useRef(false);
    var sentPlaybackStartRef = useRef(false);
    var sentSessionEndRef = useRef(false);
    var previousPausedRef = useRef(true);
    var previousWaitingRef = useRef(false);
    var previousSeekingRef = useRef(false);
    var previousQualityRef = useRef(currentQuality);
    var heartbeatDataRef = useRef({ currentTime: 0, duration: 0, playing: false, waiting: false, paused: true });
    var lastHeartbeatPositionRef = useRef(-1);
    var previousAdActiveRef = useRef(false);

    useEffect(function () {
        if (adActive) {
            previousAdActiveRef.current = true;
            player.pause();
            return;
        }

        if (previousAdActiveRef.current) {
            previousAdActiveRef.current = false;
            if (Number.isFinite(resumeTime) && resumeTime > 0) {
                player.seek(resumeTime);
            }
            player.play();
        }
    }, [adActive, player, resumeTime]);

    var getAnalyticsBaseEvent = useCallback(function () {
        var contentData = rawData?.rawData || {};
        var kind = contentData?.contentType?.kind || 'movie';
        var mode = isLive ? 'live' : 'vod';
        var event = {
            playbackSessionId: playbackSessionIdRef.current,
            mode: mode,
            contentKey: contentData.slug,
            contentKind: isLive ? 'live' : kind,
            positionSec: Math.floor(player?.get ? (player.get('currentTime') || 0) : (heartbeatDataRef.current.currentTime || 0)),
            durationSec: Math.floor(player?.get ? (player.get('duration') || 0) : (heartbeatDataRef.current.duration || 0)),
            player: 'hlsjs',
            cdn: isLive ? 'lcdn' : 'mcdn',
        };

        if (isLive) {
            event.liveEventId = rawData?.liveEventId || rawData?.rawData?.liveEventId || contentData.slug;
            event.streamKey = rawData?.streamKey || rawData?.movie?.streamKey || contentData.slug;
            event.isAtLiveEdge = true;
        }

        return event;
    }, [rawData, isLive, player]);

    var trackMediaEvent = useCallback(function (eventType, extra) {
        var baseEvent = getAnalyticsBaseEvent();
        if (!baseEvent.contentKey) return;

        sendMediaEvent(Object.assign({}, baseEvent, {
            eventType: eventType,
        }, extra || {}));
    }, [getAnalyticsBaseEvent]);

    var finishAnalyticsSession = useCallback(function () {
        var baseEvent = getAnalyticsBaseEvent();
        if (sentSessionEndRef.current || !baseEvent.contentKey) return;
        sentSessionEndRef.current = true;
        sendSessionEnd(baseEvent);
    }, [getAnalyticsBaseEvent]);

    useEffect(function () {
        var baseEvent = getAnalyticsBaseEvent();
        if (baseEvent.contentKey && !isTrailer) {
            sendMediaEvent(Object.assign({}, baseEvent, {
                eventType: 'session_start',
            }));
        }

        return function () {
            if (!isTrailer) {
                finishAnalyticsSession();
            }
        };
    }, [getAnalyticsBaseEvent, finishAnalyticsSession, isTrailer]);

    useEffect(function () {
        heartbeatDataRef.current = {
            currentTime: currentTime || 0,
            duration: duration || 0,
            playing: playing,
            waiting: waiting,
            paused: paused,
        };
    }, [currentTime, duration, playing, waiting, paused]);

    useEffect(function () {
        if (isTrailer || !isReady || sentManifestRef.current) return;
        sentManifestRef.current = true;
        trackMediaEvent('manifest_loaded');
    }, [isReady, isTrailer, trackMediaEvent]);

    useEffect(function () {
        if (isTrailer) return;

        if (playing && !sentPlaybackStartRef.current) {
            sentPlaybackStartRef.current = true;
            trackMediaEvent('playback_start');
        } else if (!previousPausedRef.current && paused) {
            trackMediaEvent('pause');
        } else if (previousPausedRef.current && !paused && sentPlaybackStartRef.current) {
            trackMediaEvent('resume');
        }

        previousPausedRef.current = paused;
    }, [playing, paused, isTrailer, trackMediaEvent]);

    useEffect(function () {
        if (isTrailer) return;

        if (!previousWaitingRef.current && waiting) {
            trackMediaEvent('buffer_start');
        } else if (previousWaitingRef.current && !waiting) {
            trackMediaEvent('buffer_end');
        }

        previousWaitingRef.current = waiting;
    }, [waiting, isTrailer, trackMediaEvent]);

    useEffect(function () {
        if (isTrailer) return;

        if (previousSeekingRef.current && !seeking) {
            trackMediaEvent('seek', {
                metadata: {
                    positionSec: Math.floor(currentTime || 0),
                },
            });
        }

        previousSeekingRef.current = seeking;
    }, [seeking, currentTime, isTrailer, trackMediaEvent]);

    useEffect(function () {
        if (isTrailer || previousQualityRef.current === currentQuality) return;

        previousQualityRef.current = currentQuality;
        trackMediaEvent('quality_change', {
            qualityLabel: String(currentQuality?.height || currentQuality?.id || currentQuality?.label || currentQuality?.bitrate || currentQuality || ''),
            bitrate: currentQuality?.bitrate,
        });
    }, [currentQuality, isTrailer, trackMediaEvent]);

    useEffect(function () {
        if (isTrailer || !error) return;
        trackMediaEvent('error', {
            metadata: {
                errorCode: error.code || error.type || '',
                errorMessage: error.message || error.details || 'Unknown error',
            },
        });
    }, [error, isTrailer, trackMediaEvent]);

    useEffect(function () {
        if (isTrailer || !ended) return;
        finishAnalyticsSession();
    }, [ended, isTrailer, finishAnalyticsSession]);

    useEffect(function () {
        if (isTrailer) return;

        var intervalId = setInterval(function () {
            var data = heartbeatDataRef.current;
            var position = Math.floor(data.currentTime || 0);
            if (!data.playing || data.paused || data.waiting) return;
            if (!isLive && position <= 0) return;
            if (!isLive && position === lastHeartbeatPositionRef.current) return;

            lastHeartbeatPositionRef.current = position;
            trackMediaEvent('heartbeat', {
                positionSec: position,
                durationSec: Math.floor(data.duration || 0),
                heartbeatIntervalSec: 10,
            });
        }, 10000);

        return function () {
            clearInterval(intervalId);
        };
    }, [isLive, isTrailer, trackMediaEvent]);

    useEffect(function () {
        if (onPlayingChange) {
            onPlayingChange(!paused);
        }
    }, [paused, onPlayingChange]);

    var getTitle = function () {
        var kind = rawData?.rawData?.contentType?.kind;
        if (!kind) return '';
        var titles = { movie: 'فیلم', series: 'سریال', program: 'برنامه', music: 'موسیقی' };
        return (titles[kind] || '') + ' ' + (rawData?.rawData?.title || '');
    };

    var getSubtitle = function () {
        var kind = rawData?.rawData?.contentType?.kind;
        if (kind === 'series' || kind === 'program') {
            return 'فصل ' + params.s + ' قسمت ' + params.e;
        }
        return null;
    };

    // Next Episode Logic (series/program)
    var nextEpisode = useMemo(function () {
        var kind = rawData?.rawData?.contentType?.kind;
        if (kind !== 'series' && kind !== 'program') return null;

        var seriesInfo = rawData?.rawData?.contentType?.seriesInfo || [];
        var nextEp = seriesInfo.find(function (x) {
            return x.episodeNumber === Number(params.e) + 1 && x.seasonNumber === Number(params.s);
        });
        if (nextEp) return nextEp;

        return seriesInfo.find(function (x) {
            return x.episodeNumber === 1 && x.seasonNumber === Number(params.s) + 1;
        });
    }, [rawData, params]);

    // Next Music State
    var [sessionMusicGuide, setSessionMusicGuide] = useState(null);
    var [nextMusic, setNextMusic] = useState(null);
    var [hasNextMusic, setHasNextMusic] = useState(false);
    var [showNextMusicCard, setShowNextMusicCard] = useState(false);
    var [showNextEpisodeCard, setShowNextEpisodeCard] = useState(false);
    var [nextEpisodeDismissed, setNextEpisodeDismissed] = useState(false);

    // Review State
    var [hasCheckedReview, setHasCheckedReview] = useState(false);
    var [userHasReviewed, setUserHasReviewed] = useState(false);
    var [showReviewCard, setShowReviewCard] = useState(false);
    var [hasShownReviewCard, setHasShownReviewCard] = useState(false);
    // Sequential transition: Review -> Next Content
    var [showNextContentAfterReview, setShowNextContentAfterReview] = useState(false);

    // User activity detection - show controls and start hide timer
    useEffect(function () {
        if (!player) return;

        var handleActivity = function () {
            player.onUserActivity();
        };

        // Show controls on mouse movement
        window.addEventListener('mousemove', handleActivity);
        // Show controls on keyboard press
        window.addEventListener('keydown', handleActivity);

        return function () {
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keydown', handleActivity);
        };
    }, [player]);

    // Flexible language code matching: handles locale vs base code mismatches
    // e.g. 'en-US' matches 'en', 'fa-IR' matches 'fa', 'en' matches 'en-US'
    var matchLangCode = function (trackLang, behaviorLang) {
        if (!trackLang || !behaviorLang) return false;
        var a = trackLang.toLowerCase();
        var b = behaviorLang.toLowerCase();
        if (a === b) return true;
        // Check if one starts with the other (base code match)
        return a.startsWith(b.split('-')[0]) || b.startsWith(a.split('-')[0]);
    };

    var isSubtitleOffValue = function (value) {
        var normalized;

        if (value === false) return true;
        if (value == null) return false;

        normalized = String(value).toLowerCase();
        return normalized === 'off' ||
            normalized === 'none' ||
            normalized === 'disabled' ||
            normalized === 'false' ||
            normalized === '0' ||
            normalized === '-1' ||
            normalized === 'خاموش';
    };

    // Auto-select default subtitle language from behavior
    useEffect(function () {
        var defaultSubLang = rawData?.behavior?.defaultSubLang;
        if (!defaultSubLang || defaultSubAppliedRef.current || !textTracks || textTracks.length === 0) return;
        if (isSubtitleOffValue(defaultSubLang)) {
            player.disableTextTracks();
            defaultSubAppliedRef.current = true;
            return;
        }
        var matchIndex = textTracks.findIndex(function (t) { return matchLangCode(t.language, defaultSubLang); });
        if (matchIndex !== -1) {
            console.log('[Behavior] Auto-selecting subtitle track:', defaultSubLang, 'at index', textTracks[matchIndex].index);
            player.setTextTrack(textTracks[matchIndex].index);
            defaultSubAppliedRef.current = true;
        }
    }, [textTracks, rawData, player]);

    // Auto-select default audio language from behavior
    useEffect(function () {
        var defaultAudioLang = rawData?.behavior?.defaultAudioLang;
        if (!defaultAudioLang || defaultAudioAppliedRef.current || !audioTracks || audioTracks.length === 0) return;
        var matchIndex = audioTracks.findIndex(function (t) { return matchLangCode(t.language, defaultAudioLang); });
        if (matchIndex !== -1) {
            console.log('[Behavior] Auto-selecting audio track:', defaultAudioLang, 'at index', audioTracks[matchIndex].index);
            player.setAudioTrack(audioTracks[matchIndex].index);
            defaultAudioAppliedRef.current = true;
        }
    }, [audioTracks, rawData, player]);

    // Show controls when paused, hide when playing
    useEffect(function () {
        if (!player) return;

        if (paused) {
            player.showControls();  // Show controls when paused, don't auto-hide
        } else {
            player.hideControls(5000);  // When playing starts, schedule hide after 5s
        }
    }, [paused, player]);

    // Load next music using NextPlayGuideHelper
    var loadNextMusic = useCallback(async function (contentData) {
        if (!contentData || contentData?.contentType?.kind !== 'music') {
            setSessionMusicGuide(null);
            return;
        }

        console.log('[NextMusic] Loading next music...');

        try {
            // Get or create session guide
            var guide = sessionMusicGuide || NextPlayGuideHelper.getNextPlayGuide(contentData, entryPointGuide);
            if (!sessionMusicGuide) {
                setSessionMusicGuide(guide);
                console.log('[NextMusic] Created session guide:', guide.type, guide.slug);
            }

            // Mark current as played
            await NextPlayGuideHelper.markAsPlayed(contentData.slug, guide);

            // Build query and fetch
            var queryParams = NextPlayGuideHelper.buildQueryParameters(guide);
            queryParams.pageIndex = 20;
            queryParams.pageNumber = 1;


            var response = await httpService.get(getCMSApiRoute('content'), { params: queryParams });
            var results = response?.data?.data?.results || [];

            console.log('[NextMusic] Fetched', results.length, 'music items');

            // Filter and select
            var filterResult = NextPlayGuideHelper.filterPlayedItems(
                results,
                contentData.slug,
                guide
            );

            if (filterResult.historyWasReset) {
                console.log('[NextMusic] Playback history was reset (looped playlist)');
            }

            var nextItem = NextPlayGuideHelper.selectNextMusic(filterResult.items);

            if (nextItem) {
                console.log('[NextMusic] Next music:', nextItem.title, '(' + nextItem.slug + ')');

                // Extract cover image from contentPhotos (match getCoverImageUrl pattern)
                var photos = nextItem.contentPhotos || [];
                var cover = photos.find(function (x) {
                    return x.type === 'squareCover' && x.device === 'TV';
                }) || photos.find(function (x) {
                    return x.type === 'squareCover';
                }) || photos.find(function (x) {
                    return x.type === 'verticalCover';
                }) || photos[0];

                var coverImage = cover ? (cover.webp || cover.src || cover.path) : null;

                setHasNextMusic(true);
                setNextMusic({
                    slug: nextItem.slug,
                    title: nextItem.title,
                    titleEN: nextItem.titleEN,
                    coverImage: coverImage,
                });
            } else {
                console.log('[NextMusic] No next music found');
                setHasNextMusic(false);
                setNextMusic(null);
            }
        } catch (error) {
            console.error('[NextMusic] Error loading:', error);
            setHasNextMusic(false);
            setNextMusic(null);
        }
    }, [sessionMusicGuide, entryPointGuide]);

    // Check if user has liked/disliked current content using content action API
    var checkUserReview = useCallback(async function (contentData, season, episode) {
        if (hasCheckedReview) return;

        // Skip for content types that don't support reviews
        if (!ReviewHelper.shouldShowReviewForContent(contentData)) {
            console.log('[Review] Disabled for this content type');
            setHasCheckedReview(true);
            setUserHasReviewed(true);
            return;
        }

        var kind = contentData?.contentType?.kind;
        var isEpisode = kind === 'series' || kind === 'program';

        // Check content action API for like/dislike status
        try {
            var response = await httpService.get(getCMSApiRoute('action/content'), {
                params: { contentKey: contentData.slug }
            });

            if (response.status === 200) {
                var actionData = response.data?.data;
                var likeOrDislikeArray = actionData?.userActions?.likeOrDislike || [];

                // For series/program, find the matching season/episode entry
                // For movies and other content, use the first entry
                var likeOrDislikeValue = null;

                if (isEpisode && season && episode) {
                    // Find matching episode in the array
                    var matchingEntry = likeOrDislikeArray.find(function (entry) {
                        return entry.season === Number(season) && entry.episode === Number(episode);
                    });
                    likeOrDislikeValue = matchingEntry?.likeOrDislike;
                    console.log('[Review] Looking for S' + season + 'E' + episode + ' - found:', matchingEntry);
                } else {
                    // Non-episode content: use first entry
                    likeOrDislikeValue = likeOrDislikeArray[0]?.likeOrDislike;
                }

                // Check if user has liked or disliked (1=like, 2=dislike)
                var hasReviewed = likeOrDislikeValue === 1 || likeOrDislikeValue === 2;

                console.log('[Review] Content action check - user has liked/disliked:', hasReviewed, 'value:', likeOrDislikeValue);
                setHasCheckedReview(true);
                setUserHasReviewed(hasReviewed);
            } else {
                console.warn('[Review] Content action check - unexpected status:', response.status);
                setHasCheckedReview(true);
                setUserHasReviewed(false);
            }
        } catch (error) {
            console.error('[Review] Content action check failed:', error);
            // On error, allow review (fail open)
            setHasCheckedReview(true);
            setUserHasReviewed(false);
        }
    }, [hasCheckedReview]);

    // Load next content when rawData changes (skip for trailers)
    useEffect(function () {
        if (isTrailer) return;
        var kind = rawData?.rawData?.contentType?.kind;
        if (kind === 'music') {
            loadNextMusic(rawData.rawData);
        }
    }, [rawData, loadNextMusic, isTrailer]);

    // Check review when content loads (skip for trailers)
    useEffect(function () {
        if (isTrailer) return;
        if (rawData?.rawData) {
            checkUserReview(rawData.rawData, params.s, params.e);
        }
    }, [rawData, params.s, params.e, checkUserReview, isTrailer]);

    // Watch Tracker - Reports progress every 60 seconds (matches Flutter TV)
    var watchTrackerRef = useRef({
        intervalId: null,
        sentInitialEvent: false,
        lastReportedMinute: -1,
    });

    useEffect(function () {
        console.log('[WatchTracker] Effect running, slug:', rawData?.rawData?.slug, 'isTrailer:', isTrailer, 'isLive:', isLive);

        if (!rawData?.rawData?.slug || isTrailer) {
            console.log('[WatchTracker] Skipping - no slug or is trailer');
            return;
        }

        var tracker = watchTrackerRef.current;
        var slug = rawData.rawData.slug;
        var kind = rawData.rawData.contentType?.kind;
        var isEpisode = kind === 'series' || kind === 'program';
        var season = params.s ? Number(params.s) : null;
        var episode = params.e ? Number(params.e) : null;

        // Skip for music content (only track video)
        if (kind === 'music') {
            console.log('[WatchTracker] Skipping - music content');
            return;
        }

        // Send initial watch event when content starts (including for live streams)
        if (!tracker.sentInitialEvent) {
            tracker.sentInitialEvent = true;
            console.log('[WatchTracker] Sending initial watch event for:', slug);
            sendWatchEvent(slug);
        }

        // Start 60-second interval for progress reporting (disabled for live streams)
        if (!tracker.intervalId && !isLive) {
            console.log('[WatchTracker] Starting 60s interval timer');
            tracker.intervalId = setInterval(function () {
                // Use player.get() to get current time from PlayerState
                var currentTimeSeconds = player?.get ? player.get('currentTime') : 0;
                var currentMinutes = Math.floor(currentTimeSeconds / 60);

                console.log('[WatchTracker] Interval tick - currentTime:', currentTimeSeconds, 'minutes:', currentMinutes, 'lastReported:', tracker.lastReportedMinute);

                // Only report if time has changed and more than 0
                if (currentMinutes !== tracker.lastReportedMinute && currentMinutes > 0) {
                    tracker.lastReportedMinute = currentMinutes;
                    console.log('[WatchTracker] Sending progress report:', currentMinutes, 'minutes');
                    sendWatchTracker({
                        slug: slug,
                        minutes: currentMinutes,
                        isEpisode: isEpisode,
                        season: season,
                        episode: episode,
                    });
                }
            }, 60000); // 60 seconds
        }

        // Cleanup: send final report and refetch continue-watch
        return function () {
            console.log('[WatchTracker] Cleanup - clearing interval');
            if (tracker.intervalId) {
                clearInterval(tracker.intervalId);
                tracker.intervalId = null;
            }

            // Send final watch tracker report (disabled for live streams)
            if (!isLive) {
                var finalTimeSeconds = player?.get ? player.get('currentTime') : 0;
                if (finalTimeSeconds > 0) {
                    var finalMinutes = Math.floor(finalTimeSeconds / 60);
                    if (finalMinutes > 0 && finalMinutes !== tracker.lastReportedMinute) {
                        console.log('[WatchTracker] Sending final report:', finalMinutes, 'minutes');
                        sendWatchTracker({
                            slug: slug,
                            minutes: finalMinutes,
                            isEpisode: isEpisode,
                            season: season,
                            episode: episode,
                        });
                    }
                }

                // Refetch continue-watch data for home page
                console.log('[WatchTracker] Refetching continue-watch');
                refetchContinueWatch();
            }
        };
    }, [rawData, params.s, params.e, player, isTrailer, isLive]);

    useEffect(function () {
        if (!duration || duration === 0 || isTrailer) return;

        var kind = rawData?.rawData?.contentType?.kind;
        var localHotPoints = hotPoints || [];

        // Debug: Log music card timing check periodically
        if (kind === 'music' && Math.floor(currentTime) % 10 === 0) {
            var timing = HotpointHelper.getNextContentTriggerTime(duration, localHotPoints, 'music');
            console.log('[NextMusicDebug] Time:', Math.floor(currentTime), '/', duration,
                'hasNextMusic:', hasNextMusic,
                'triggerTime:', timing.triggerTime,
                'useHotpoint:', timing.useHotpoint,
                'hotPointsCount:', localHotPoints.length);
        }

        // Music: show in outro or last 15 seconds
        if (kind === 'music' && hasNextMusic) {
            var shouldShow = HotpointHelper.shouldShowNextContent(
                currentTime,
                duration,
                localHotPoints,
                'music'
            );

            if (shouldShow && !showNextMusicCard) {
                console.log('[NextMusic] Showing card at', currentTime);
                setShowNextMusicCard(true);
            } else if (!shouldShow && showNextMusicCard) {
                setShowNextMusicCard(false);
            }
        }

        // Series/Program: show in outro or last 45 seconds
        // Sequential flow: If user already reviewed -> show next episode immediately
        //                  If user hasn't reviewed -> show after review card dismisses
        if ((kind === 'series' || kind === 'program') && nextEpisode) {
            var shouldShowEp = HotpointHelper.shouldShowNextContent(
                currentTime,
                duration,
                localHotPoints,
                kind
            );

            // If user already reviewed, show next episode immediately at outro
            // Otherwise, wait for review card to finish (showNextContentAfterReview)
            if (shouldShowEp) {
                if (userHasReviewed || hasShownReviewCard) {
                    // Already reviewed OR review was already shown - show next episode
                    if (!nextEpisodeDismissed && !showNextEpisodeCard && (userHasReviewed || showNextContentAfterReview)) {
                        console.log('[NextEpisode] Showing card at', currentTime, '(review done or skipped)');
                        setShowNextEpisodeCard(true);
                    }
                }
                // If review card is still active, don't show next episode yet
            } else {
                if (showNextEpisodeCard) {
                    setShowNextEpisodeCard(false);
                }
            }

            // DEBUG: Trace timing logic
            if (Math.floor(currentTime) % 5 === 0) {
                console.log('[NextEpisodeDebug] Time:', Math.floor(currentTime), '/', duration,
                    'ShouldShow:', shouldShowEp, 'ReviewActive:', showReviewCard,
                    'UserReviewed:', userHasReviewed, 'ShowAfterReview:', showNextContentAfterReview);
            }
        }

        // Review Card: show AT outro start for 15 seconds
        // Music and AnaVatan are already excluded via shouldShowReviewForContent check
        if (!userHasReviewed && !hasShownReviewCard && ReviewHelper.shouldShowReviewForContent(rawData?.rawData)) {
            // Find outro hotpoint
            var outroHotpoint = localHotPoints.find(function (x) {
                return x.hotpointType === 'outro' || x.type === 'outro';
            });

            var outroStartTime;

            if (outroHotpoint && outroHotpoint.startTime) {
                outroStartTime = outroHotpoint.startTime;
            } else {
                // If no outro hotpoint:
                // - Music: last 15 seconds (but music is excluded from reviews)
                // - All others: last 45 seconds
                var fallbackSeconds = kind === 'music' ? 15 : 45;
                outroStartTime = Math.max(0, duration - fallbackSeconds);
            }

            // Review card starts AT outro (not before) and shows for 15 seconds
            // The ReviewCard component handles the 15s countdown internally
            var shouldShowReview =
                currentTime >= outroStartTime &&
                !showReviewCard;

            if (shouldShowReview) {
                console.log('[Review] Showing review card at', currentTime, '(outro at', outroStartTime, ')');
                setShowReviewCard(true);
                setHasShownReviewCard(true);
            }
        }
    }, [currentTime, duration, hasNextMusic, nextEpisode, showNextMusicCard, showNextEpisodeCard, rawData, hotPoints, hasCheckedReview, userHasReviewed, showReviewCard, hasShownReviewCard, showNextContentAfterReview, nextEpisodeDismissed, isTrailer]);

    useEffect(function () {
        setNextEpisodeDismissed(false);
    }, [rawData?.rawData?.slug, params.s, params.e]);

    // Update spatial navigation tree when any overlay visibility changes
    useEffect(function () {
        updateAllLayouts();
    }, [showNextMusicCard, showNextEpisodeCard, showReviewCard]);

    // Get cover image URL for review card (Poster/Vertical only - 13/9 ratio)
    var getCoverImageUrl = function (contentData) {
        if (!contentData) return null;

        // Prioritize strictly vertical covers
        var photos = contentData.contentPhotos || contentData.meta?.contentPhotos || [];

        // 1. verticalCover with TV preference
        var cover = photos.find(function (x) {
            return x.type === 'verticalCover' && x.device === 'TV';
        });

        // 1b. any verticalCover
        if (!cover) {
            cover = photos.find(function (x) {
                return x.type === 'verticalCover';
            });
        }

        // 2. videoPlayerCover
        if (!cover) {
            cover = photos.find(function (x) {
                return x.type === 'videoPlayerCover';
            });
        }

        // 3. cover (standard poster)
        if (!cover) {
            cover = photos.find(function (x) {
                return x.type === 'cover';
            });
        }

        // 4. Fallback to first photo
        if (!cover && photos.length > 0) {
            cover = photos[0];
        }

        if (cover) {
            // Check for webp, src, or path (match Flutter TV pattern)
            var imagePath = cover.webp || cover.src || cover.path;
            if (imagePath) {
                return getCMSImageURL(imagePath);
            }
        }

        return null;
    };

    // Submit like/dislike using content action API
    var submitReview = useCallback(async function (reviewType) {
        try {
            var contentData = rawData?.rawData;
            if (!contentData) return;

            var kind = contentData.contentType?.kind;
            var isEpisode = kind === 'series' || kind === 'program';

            // Use like or dislike API based on reviewType (1=like, 2=dislike)
            var apiEndpoint = reviewType === 1 ? 'action/like' : 'action/dislike';
            var body = {
                contentKey: contentData.slug,
            };

            if (isEpisode) {
                body.isEpisode = 1
                body.season = Number(params.s);
                body.episode = Number(params.e);
            }

            console.log('[Review] Submitting like/dislike:', reviewType === 1 ? 'like' : 'dislike', body);
            var response = await httpService.post(getCMSApiRoute(apiEndpoint), body);

            if (response.status === 200 || response.status === 201) {
                console.log('[Review] Like/dislike submitted successfully');

                // Cache locally
                ReviewHelper.cacheReview(
                    contentData.slug,
                    reviewType,
                    isEpisode,
                    params.s,
                    params.e
                );

                // Refetch content action to update UI state and invalidate cache
                try {
                    var refreshResponse = await httpService.get(getCMSApiRoute('action/content'), {
                        params: { contentKey: contentData.slug }
                    });
                    console.log('[Review] Content action refetched after submission:', refreshResponse.data?.data);
                } catch (refreshError) {
                    console.warn('[Review] Failed to refetch content action:', refreshError);
                }

                setUserHasReviewed(true);
            } else {
                console.warn('[Review] Like/dislike submit failed:', response.status, response.data);
            }
        } catch (error) {
            console.error('[Review] Like/dislike submit error:', error);
        }
    }, [rawData, params]);

    var continueWatchTotalTime = getWatchTotalMinutes(watchTrackData);

    return React.createElement(React.Fragment, null,
        React.createElement(MnbPlayer, {
            source: source,
            subtitle: subtitle,
            behavior: rawData?.behavior,
            poster: poster,
            autoPlay: true,
            onEnded: onEnded,
            onError: onError,
            isLive: isLive,
        },
            React.createElement(ControlsOverlay, {
                title: isTrailer ? 'پیش نمایش ' + (rawData?.rawData?.title || '') : getTitle(),
                subtitle: isTrailer ? '' : getSubtitle(),
                onSettingsPress: function () { setSettingsOpen(true); },
                onCaptionsPress: function () { setCaptionsOpen(true); },
                onEpisodesPress: function () { setEpisodesOpen(true); },
                showEpisodesButton: !isTrailer && (rawData?.rawData?.contentType?.kind === 'series' || rawData?.rawData?.contentType?.kind === 'program'),
                hideSettings: isTrailer,
                hideCaptions: isTrailer,
                isLive: isLive,
            }),
            !isTrailer && React.createElement('div', { className: 'mnb-overlay-buttons' },
                React.createElement(SkipIntroButton, { hotPoints: hotPoints }),
                !isLive && (autoResume ?
                    React.createElement(AutoResumeHandler, {
                        elapsedTime: watchTrackData?.options?.elapsedTime,
                        totalTime: continueWatchTotalTime,
                    }) :
                    React.createElement(ContinueWatchButton, {
                        elapsedTime: watchTrackData?.options?.elapsedTime,
                        totalTime: continueWatchTotalTime,
                    }))
            ),
            // Watermark - behind controls layer, always visible when enabled
            rawData?.behavior?.watermark && React.createElement('div', {
                className: 'mnb-watermark',
                style: { position: 'absolute', top: '40px', right: '40px', zIndex: 1, opacity: 0.5, pointerEvents: 'none' },
            }, React.createElement('img', {
                src: '/images/mnb-watermark.svg',
                alt: 'watermark',
                style: { width: '80px' },
            })),
            React.createElement(LoadingIndicator),
            React.createElement(PlayerDPad),
            // React.createElement(ControlsDebugOverlay)
        ),
        React.createElement(Settings, {
            isOpen: settingsOpen,
            onClose: function () { setSettingsOpen(false); },
            type: 'quality',
        }),
        React.createElement(Settings, {
            isOpen: captionsOpen,
            onClose: function () { setCaptionsOpen(false); },
            type: 'captions',
            restoreFocusKey: 'captionsButton',
        }),
        React.createElement(EpisodeSwitcher, {
            isOpen: episodesOpen,
            onClose: function () { setEpisodesOpen(false); },
            seriesInfo: rawData?.rawData?.contentType?.seriesInfo,
            currentSeason: params.s,
            currentEpisode: params.e,
            contentSlug: rawData?.rawData?.slug,
            contentTitle: rawData?.rawData?.title,
            restoreFocusKey: 'episodesButton',
        }),

        // Next Music Card (from parent state) - disabled for trailers
        !isTrailer && React.createElement(NextMusicCard, {
            nextMusic: nextMusic,
            onPlay: function () {
                if (!nextMusic) return;
                console.log('[NextMusic] Playing next:', nextMusic.slug);
                // Navigate to next music content
                var guide = sessionMusicGuide || entryPointGuide;
                navigate('/watch/' + nextMusic.slug, {
                    replace: true,
                    state: NextPlayGuideHelper.withGuideNavigationState(location.state, guide),
                });
            },
            onDismiss: function () {
                console.log('[NextMusic] Dismissed');
                setShowNextMusicCard(false);
            },
            remainingSeconds: showNextMusicCard ? Math.max(0, Math.floor(duration - currentTime)) : 0,
            visible: showNextMusicCard && hasNextMusic,
        }),

        // Next Episode Card - disabled for trailers
        !isTrailer && React.createElement(NextEpisodeCard, {
            nextEpisode: nextEpisode,
            slug: rawData?.rawData?.slug,
            visible: showNextEpisodeCard && !nextEpisodeDismissed,
            onDismiss: function () {
                setNextEpisodeDismissed(true);
                setShowNextEpisodeCard(false);
            },
        }),

        // Review Card - disabled for trailers
        !isTrailer && React.createElement(ReviewCard, {
            coverImageUrl: getCoverImageUrl(rawData?.rawData),
            contentType: rawData?.rawData?.contentType?.kind || 'movie',
            title: rawData?.rawData?.title,
            season: params.s ? Number(params.s) : null,
            episode: params.e ? Number(params.e) : null,
            visible: showReviewCard && !userHasReviewed,
            onSubmit: function (reviewType) {
                submitReview(reviewType);
                console.log('[Review] Submitted, transitioning to next content');
            },
            onDismiss: function () {
                console.log('[Review] Dismissed, transitioning to next content');
                setShowReviewCard(false);
                // Trigger next content card after review dismissal
                setShowNextContentAfterReview(true);
            },
            displayDuration: 10,
        }),

        // Ad Handler - monitors time and triggers ads
        adCreatives && adCreatives.length > 0 && onTriggerAds && React.createElement(AdHandler, {
            currentTime: currentTime,
            duration: duration,
            creatives: adCreatives,
            scheduledCreativeIds: scheduledCreativeIds,
            adActive: adActive,
            isLive: isLive,
            onTriggerAds: onTriggerAds,
        })
    );
}

/**
 * Parse and clean HLS playlist (remove 4K)
 */
async function parseHLSPlaylist(masterUrl) {
    try {
        var response = await fetch(masterUrl);
        var text = await response.text();
        var baseUri = masterUrl.substring(0, masterUrl.lastIndexOf('/') + 1);

        var lines = text.split('\n');
        var cleanedLines = [];
        var skipNext = false;

        for (var i = 0; i < lines.length; i++) {
            var line = lines[i];

            // Skip 4K
            if (line.indexOf('#EXT-X-STREAM-INF') !== -1 && line.indexOf('RESOLUTION=3840x2160') !== -1) {
                skipNext = true;
                continue;
            }
            if (skipNext) {
                skipNext = false;
                continue;
            }

            // Fix relative URIs
            if (line.indexOf('#EXT-X-MEDIA') === 0 && line.indexOf('URI="') !== -1) {
                line = line.replace(/URI="([^"]+)"/, function (match, uri) {
                    return 'URI="' + new URL(uri, baseUri).toString() + '"';
                });
            }

            if (line.trim() && line.indexOf('#') !== 0 && line.indexOf('http') !== 0) {
                line = new URL(line, baseUri).toString();
            }

            cleanedLines.push(line);
        }

        var blob = new Blob([cleanedLines.join('\n')], { type: 'application/x-mpegurl' });
        return { src: URL.createObjectURL(blob), type: 'application/x-mpegURL' };
    } catch (err) {
        console.error('[PlayerContent] Failed to parse HLS:', err);
        return { src: masterUrl, type: 'application/x-mpegURL' };
    }
}

/**
 * Main PlayerContent Component
 */
/**
 * Main PlayerContent Component
 */
function PlayerContent(props) {
    var data = props.data;
    var params = useParams();
    var isTrailer = data?.isTrailer === true;
    var isLive = data?.isLive === 1 || data?.isLive === true || data?.rawData?.isLive === 1 || data?.rawData?.isLive === true;

    // --- AutoResume Logic Start ---

    // Check for autoResume via encrypted cookie
    var checkAutoResumeCookie = function () {
        try {
            var cookies = document.cookie.split(';');
            var COOKIE_KEY = 'mnb_ar';
            var ENCRYPTION_KEY = 'M4n4b4kh_s3cur3_k3y_2024';

            var cookieValue = null;
            for (var i = 0; i < cookies.length; i++) {
                var cookie = cookies[i].trim();
                if (cookie.indexOf(COOKIE_KEY + '=') === 0) {
                    cookieValue = cookie.substring(COOKIE_KEY.length + 1);
                    break;
                }
            }

            console.log('[AutoResume DEBUG] Cookie found:', !!cookieValue);
            if (!cookieValue) return false;

            // Decrypt first, then validate before deleting
            var base64 = cookieValue.replace(/-/g, '+').replace(/_/g, '/');
            while (base64.length % 4) base64 += '=';
            var decoded = atob(base64);
            var result = '';
            for (var j = 0; j < decoded.length; j++) {
                var charCode = decoded.charCodeAt(j) ^ ENCRYPTION_KEY.charCodeAt(j % ENCRYPTION_KEY.length);
                result += String.fromCharCode(charCode);
            }

            console.log('[AutoResume DEBUG] Decrypted result:', result);

            // Validate timestamp (5 minute max)
            var parts = result.split(':');
            var timestamp = parseInt(parts[0], 36);
            var age = Date.now() - timestamp;
            console.log('[AutoResume DEBUG] Cookie age (ms):', age, 'Max allowed:', 300000);

            if (age > 300000) {
                // Expired - delete and return false
                document.cookie = COOKIE_KEY + '=; path=/; max-age=0';
                console.log('[AutoResume DEBUG] Cookie expired, returning false');
                return false;
            }

            // Parse and verify content match
            var jsonStr = parts.slice(1).join(':');
            console.log('[AutoResume DEBUG] JSON string:', jsonStr);
            var payload = JSON.parse(jsonStr);
            console.log('[AutoResume DEBUG] Parsed payload:', payload);

            var contentSlug = data?.rawData?.slug;
            console.log('[AutoResume DEBUG] Content slug from data:', contentSlug);
            console.log('[AutoResume DEBUG] params.s:', params.s, 'params.e:', params.e);

            if (!contentSlug) {
                console.log('[AutoResume DEBUG] No content slug, returning false');
                return false;
            }

            var isMatch = false;

            // For non-series, just match slug
            if (!payload.season && !payload.episode) {
                isMatch = payload.slug === contentSlug;
                console.log('[AutoResume DEBUG] Non-series match:', isMatch, 'payload.slug:', payload.slug, 'contentSlug:', contentSlug);
            } else {
                // For series, match slug + season + episode
                isMatch = payload.slug === contentSlug &&
                    String(payload.season) === String(params.s) &&
                    String(payload.episode) === String(params.e);
                console.log('[AutoResume DEBUG] Series match:', isMatch,
                    'slug:', payload.slug, '==', contentSlug,
                    'season:', payload.season, '==', params.s,
                    'episode:', payload.episode, '==', params.e);
            }

            // Only delete cookie after successful match
            if (isMatch) {
                document.cookie = COOKIE_KEY + '=; path=/; max-age=0';
                console.log('[AutoResume] Cookie matched and consumed');
            }

            return isMatch;
        } catch (e) {
            console.error('[AutoResume] Cookie check failed:', e);
            document.cookie = 'mnb_ar=; path=/; max-age=0'; // Clear bad cookie
            return false;
        }
    };

    // --- AutoResume Decision Logic ---
    // Use sessionStorage to persist decision across StrictMode unmount/remount cycles
    // Only store TRUE decisions, and only for a short window (30 seconds)
    var autoResumeStorageKey = 'mnb_ar_decision_' + (data?.rawData?.slug || params.slug || 'unknown');

    var getAutoResumeDecision = function () {
        if (isLive) {
            console.log('[AutoResume] Disabling auto-resume for live stream');
            return false;
        }
        var elapsedTime = data?.watchTrackData?.options?.elapsedTime;
        var totalTime = getWatchTotalMinutes(data?.watchTrackData);
        var hasElapsedTime = isContinueWatchPositionValid(elapsedTime, null, totalTime);

        // First check sessionStorage for already-made TRUE decision
        try {
            var stored = sessionStorage.getItem(autoResumeStorageKey);
            if (stored) {
                var parsed = JSON.parse(stored);
                // Check if decision is true and fresh (within 30 seconds)
                if (parsed.decision === true && hasElapsedTime && (Date.now() - parsed.timestamp < 30000)) {
                    console.log('[AutoResume] Using stored TRUE decision, age:', Date.now() - parsed.timestamp, 'ms');
                    return true;
                }
            }
        } catch (e) { /* ignore */ }

        // No valid stored decision - check cookie
        var cookieResult = checkAutoResumeCookie();
        var decision = cookieResult && hasElapsedTime;

        // Only store TRUE decisions (to prevent re-renders from overwriting)
        if (decision) {
            try {
                sessionStorage.setItem(autoResumeStorageKey, JSON.stringify({
                    decision: true,
                    timestamp: Date.now()
                }));
                console.log('[AutoResume] TRUE decision computed and stored');
            } catch (e) { /* ignore */ }
        } else {
            console.log('[AutoResume] FALSE decision (not stored):', 'cookie:', cookieResult, 'elapsedTime:', hasElapsedTime);
        }

        return decision;
    };

    var shouldAutoResume = getAutoResumeDecision();

    console.log('[AutoResume] Status:', shouldAutoResume,
        'ElapsedTime:', data?.watchTrackData?.options?.elapsedTime);


    // --- AutoResume Logic End ---

    var vast = data?.vast || null;
    var adCreatives = useMemo(function () {
        return getVastCreatives(vast);
    }, [vast]);
    var initialAdCreatives = useMemo(function () {
        return adCreatives.filter(function (creative) {
            return getCreativeTriggerTime(creative, Number.NaN) === 0;
        });
    }, [adCreatives]);
    var shouldSkipIntro =
        isTrailer || data?.behavior?.playIntro === false || shouldAutoResume;
    var startsWithInitialAds = shouldSkipIntro && initialAdCreatives.length > 0;

    // Determine initial playingTrack based on intro and pre-roll availability.
    var [playingTrack, setPlayingTrack] = useState(function () {
        if (startsWithInitialAds) return 'vast';
        return shouldSkipIntro ? 'media' : 'intro';
    });
    // Ref for immediate sync access - avoids React batching issues on Tizen
    var playingTrackRef = useRef(
        startsWithInitialAds ? 'vast' : (shouldSkipIntro ? 'media' : 'intro')
    );
    var [source, setSource] = useState(null);
    var [poster, setPoster] = useState('');
    var [ready, setReady] = useState(false);
    var [, setIsPlaying] = useState(false);

    // Advertisement state is scoped to one keyed content-player instance.
    var [scheduledCreativeIds, setScheduledCreativeIds] = useState(function () {
        var scheduled = {};
        if (startsWithInitialAds) {
            initialAdCreatives.forEach(function (creative) {
                scheduled[creative.id] = true;
            });
        }
        return scheduled;
    });
    var [adQueue, setAdQueue] = useState(
        startsWithInitialAds ? initialAdCreatives : []
    );
    var [activeAd, setActiveAd] = useState(
        startsWithInitialAds ? initialAdCreatives[0] : null
    );
    var [playerTime, setPlayerTime] = useState(0); // Time to resume after ad
    var [mainPlayerStarted, setMainPlayerStarted] = useState(
        shouldSkipIntro && !startsWithInitialAds
    );
    var trackingSessionRef = useRef(new Set());

    // Use batched reward system
    var { ref, focusKey, focusSelf } = useFocusable({
        focusKey: 'playerContent',
    });

    useEffect(function () {
        focusSelf();
    }, [focusSelf]);

    useEffect(function () {
        async function init() {
            if (data.hasErr) return;

            // Get poster
            var posters = data.posters || [];
            var posterItem = posters.find(function (x) {
                return (x.deviceF === 'TV' && x.type === 'videoPlayerCover') || x.type === 'videoPlayerCover';
            });
            if (posterItem && posterItem.src) {
                setPoster(getCMSImageURL(posterItem.src));
            }

            // Get source
            var sources = orderHlsFirst(data.movie?.sources || []);
            if (sources.length === 0) {
                console.error('[PlayerContent] No sources');
                return;
            }

            // Find HLS source
            var hlsSource = sources.find(isHlsSource);

            if (hlsSource) {
                if (isLive) {
                    setSource([{
                        src: hlsSource.src,
                        type: hlsSource.type || 'application/x-mpegURL',
                    }]);
                } else {
                    var parsedSource = await parseHLSPlaylist(hlsSource.src);
                    setSource([parsedSource]);
                }
            } else {
                setSource(sources);
            }

            setReady(true);
        }

        init();
    }, [data, isLive]);

    var startAdQueue = useCallback(function (creatives, currentPlayerTime) {
        if (!creatives || creatives.length === 0) {
            playingTrackRef.current = 'media';
            setPlayingTrack('media');
            setMainPlayerStarted(true);
            return;
        }

        setScheduledCreativeIds(function (previous) {
            var updated = Object.assign({}, previous);
            creatives.forEach(function (creative) {
                updated[creative.id] = true;
            });
            return updated;
        });
        setPlayerTime(currentPlayerTime || 0);
        setAdQueue(creatives);
        setActiveAd(creatives[0]);
        playingTrackRef.current = 'vast';
        setPlayingTrack('vast');
    }, []);

    // Handle intro end - check for preRoll VAST, otherwise go to main player
    // Uses setTimeout for Tizen/WebOS legacy Chromium compatibility:
    // - Breaks out of React batching to ensure cleanup completes first
    // - Gives legacy browser time to release IntroPlayer video resources
    var handleIntroEnd = useCallback(function () {
        // Guard: prevent duplicate transitions using ref (sync check)
        if (playingTrackRef.current !== 'intro') {
            console.log('[PlayerContent] Intro already ended, ignoring duplicate');
            return;
        }

        console.log('[PlayerContent] Intro ended, scheduling transition...');

        // Small delay ensures IntroPlayer cleanup runs before new component mounts
        // Critical for Tizen (Chromium ~30) and WebOS legacy browsers
        setTimeout(function () {
            if (initialAdCreatives.length > 0) {
                console.log('[PlayerContent] Transitioning to pre-roll creatives');
                startAdQueue(initialAdCreatives, 0);
            } else {
                console.log('[PlayerContent] Transitioning to main player');
                playingTrackRef.current = 'media'; // Update ref immediately (sync)
                setPlayingTrack('media');
                setMainPlayerStarted(true);
            }
        }, 50); // 50ms delay - enough for cleanup, not noticeable to user
    }, [initialAdCreatives, startAdQueue]);

    // Handle due creatives in backend response order.
    var handleTriggerAds = useCallback(function (creatives, currentPlayerTime) {
        console.log('[PlayerContent] Triggering creative queue at:', currentPlayerTime);
        startAdQueue(creatives, currentPlayerTime);
    }, [startAdQueue]);

    // Handle one creative ending. Continue the queue before resuming content.
    var handleAdEnd = useCallback(function () {
        if (playingTrackRef.current !== 'vast') {
            console.log('[PlayerContent] Ad already ended, ignoring duplicate');
            return;
        }

        if (adQueue.length > 1) {
            var remaining = adQueue.slice(1);
            setAdQueue(remaining);
            setActiveAd(remaining[0]);
            return;
        }

        console.log('[PlayerContent] Ad queue ended, resuming media at:', playerTime);
        playingTrackRef.current = 'media';
        setAdQueue([]);
        setActiveAd(null);
        setPlayingTrack('media');
        setMainPlayerStarted(true);
    }, [adQueue, playerTime]);

    // Track playing state from main content
    useEffect(function () {
        // When media track is active, assume playing
        if (playingTrack === 'media' && ready) {
            setIsPlaying(true);
        } else {
            setIsPlaying(false);
        }
    }, [playingTrack, ready]);

    if (data.hasErr) {
        return React.createElement('main', {
            style: {
                width: '100%',
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#000',
                color: '#fff',
                fontFamily: '"AnjomanMax", sans-serif',
            },
        }, data.message || 'محتوا یافت نشد');
    }

    // Render based on current track
    var renderPlayer = function () {
        var showIntro = playingTrack === 'intro';
        var showAd = playingTrack === 'vast' && activeAd;
        var mainPlayerReady = ready && source;
        var showMainPlayer = mainPlayerStarted;

        return React.createElement(React.Fragment, null,
            // Once started, keep the main player mounted underneath ads so its
            // buffered state and exact playback position are preserved.
            showMainPlayer && mainPlayerReady && React.createElement('div', {
                className: 'mnb-player-container',
            },
                React.createElement(PlayerProvider, null,
                    React.createElement(VideoPlayerWrapper, {
                        source: source,
                        subtitle: data.movie?.subTitles,
                        hotPoints: data.hotPoints,
                        rawData: data,
                        poster: poster,
                        watchTrackData: data.watchTrackData,
                        onPlayingChange: setIsPlaying,
                        autoResume: shouldAutoResume,
                        // Ad-related props
                        adCreatives: adCreatives,
                        scheduledCreativeIds: scheduledCreativeIds,
                        adActive: showAd,
                        onTriggerAds: handleTriggerAds,
                        resumeTime: playerTime,
                    })
                )
            ),

            // Intro player - use opacity:0 + pointer-events:none for legacy Chromium compatibility (Tizen/WebOS)
            React.createElement('div', {
                className: 'mnb-intro-overlay',
                style: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: showIntro ? 10 : -1,
                    backgroundColor: '#000',
                    opacity: showIntro ? 1 : 0,
                    pointerEvents: showIntro ? 'auto' : 'none',
                }
            },
                // Only mount IntroPlayer when showing to trigger proper lifecycle
                showIntro && React.createElement(IntroPlayer, {
                    poster: poster,
                    onPlayEnd: handleIntroEnd,
                })
            ),

            // VAST Ad player - overlay on top of main player
            showAd && React.createElement('div', {
                className: 'mnb-vast-overlay',
                style: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 20,
                    backgroundColor: '#000',
                }
            },
                React.createElement(VastPlayer, {
                    key: activeAd.id,
                    vast: vast,
                    creative: activeAd,
                    sessionFired: trackingSessionRef.current,
                    analyticsContext: {
                        content: data.rawData || data,
                        contentPosition: playerTime,
                        spotPosition: normalizeVastSlot(activeAd.creativeSlot),
                        source: 'vast_player',
                    },
                    allowSkip: data.ads?.allowSkipAd,
                    skipValue: data.ads?.skipAdValue || 5,
                    onPlayEnd: handleAdEnd,
                })
            ),

            // Loading spinner when main player not ready
            !mainPlayerReady && playingTrack === 'media' && React.createElement('div', { className: 'mnb-loading' })
        );
    };

    return React.createElement(FocusContext.Provider, { value: focusKey },
        React.createElement('main', {
            ref: ref,
            style: {
                width: '100%',
                height: '100vh',
                backgroundColor: '#000',
            },
        },
            renderPlayer(),
            // DEBUG OVERLAY - shows track state info
            // React.createElement('div', {
            //     style: {
            //         position: 'fixed',
            //         top: '10px',
            //         left: '10px',
            //         backgroundColor: 'rgba(0, 0, 0, 0.8)',
            //         color: '#0f0',
            //         padding: '10px 15px',
            //         borderRadius: '8px',
            //         fontFamily: 'monospace',
            //         fontSize: '14px',
            //         zIndex: 9999,
            //         pointerEvents: 'none',
            //         lineHeight: '1.6',
            //     }
            // },
            //     React.createElement('div', null, 'Track: ' + playingTrack),
            //     React.createElement('div', null, 'Ready: ' + (ready ? 'YES' : 'NO')),
            //     React.createElement('div', null, 'Source: ' + (source ? 'YES' : 'NO')),
            //     React.createElement('div', null, 'HasAds: ' + (hasAds ? 'YES' : 'NO')),
            //     React.createElement('div', null, 'ActiveAd: ' + (activeAd ? activeAd.key : 'NONE')),
            //     React.createElement('div', null, 'PreRoll Played: ' + (adsPlayData.preRoll ? 'YES' : 'NO'))
            // )
        )
    );
}

export default PlayerContent;
export { PlayerContent, VideoPlayerWrapper, MnbPlayer, IntroPlayer };
